import toast from 'react-hot-toast';
import { SuccessIcon } from '../icons/SuccessIcon';
import { ErrorIcon } from '../icons/ErrorIcon';
import { WarningAlertIcon } from '../icons/WarningAlertIcon';
import { CloseIcon } from '../icons/CloseIcon';

const toastStyles = {
  container: {
    pointerEvents: 'auto' as const,
    width: '100%',
    maxWidth: '377px',
    borderRadius: '4px',
    padding: '16px',
    boxShadow: '0 0 20px 0 rgba(0, 0, 0, 0.08)',
    fontFamily: '"Figtree", "Gilroy", Roboto, sans-serif',
    position: 'relative' as const,
  },
  content: {
    display: 'flex',
    alignItems: 'flex-start',
    width: '100%',
  },
  icon: {
    marginTop: '4px',
    display: 'flex',
    alignItems: 'center' as const,
    justifyContent: 'center' as const,
    width: '16px',
    height: '16px',
    flexShrink: 0,
  },
  message: {
    flex: '1 1 0%',
    paddingRight: '16px',
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '2px',
    minWidth: 0,
  },
  title: {
    fontSize: '16px',
    lineHeight: '24px',
    fontWeight: 600,
    color: 'rgb(13, 13, 70)',
    margin: 0,
    fontFamily: 'inherit',
  },
  description: {
    overflowWrap: 'break-word' as const,
    fontSize: '14px',
    lineHeight: '20px',
    fontWeight: 300,
    margin: 0,
    fontFamily: 'inherit',
  },
  close: {
    border: 'none',
    outline: 'none',
    background: 'transparent',
    cursor: 'pointer',
    padding: '4px',
    display: 'flex',
    alignItems: 'center' as const,
    justifyContent: 'center' as const,
    width: '20px',
    height: '20px',
    flexShrink: 0,
  },
};

const colorVariants = {
  green: {
    background: '#cfffeb',
    icon: '#1d7351',
    text: '#1d7351',
  },
  orange: {
    background: '#ffebd0',
    icon: '#734D19',
    text: '#734d19',
  },
  red: {
    background: '#ffdae3',
    icon: '#ff4874',
    text: '#cc3a5d',
  },
};

const createToast = (type: 'success' | 'error' | 'warning', title: string, description: string, closable = true) => {
  const variant = type === 'success' ? 'green' : type === 'error' ? 'red' : 'orange';
  const colors = colorVariants[variant];

  const getIconElement = () => {
    switch (type) {
      case 'success':
        return <SuccessIcon fill={colors.icon} width={16} height={16} />;
      case 'error':
        return <ErrorIcon fill={colors.icon} width={16} height={16} />;
      case 'warning':
        return <WarningAlertIcon fill={colors.icon} width={16} height={16} />;
      default:
        return <WarningAlertIcon fill={colors.icon} width={16} height={16} />;
    }
  };

  return toast.custom(
    (t) => (
      <div
        style={{
          ...toastStyles.container,
          backgroundColor: colors.background,
          opacity: t.visible ? 1 : 0,
          transform: `translateX(${t.visible ? 0 : '8px'})`,
          transition: 'all 300ms cubic-bezier(0.4, 0, 0.2, 1)',
        }}
      >
        <div style={toastStyles.content}>
          <div style={toastStyles.icon}>{getIconElement()}</div>
          <div style={{ ...toastStyles.message, paddingLeft: '12px' }}>
            <div style={toastStyles.title}>{title}</div>
            <div style={{ ...toastStyles.description, color: colors.text }}>{description}</div>
          </div>
          {closable && (
            <button
              style={{ ...toastStyles.close }}
              onClick={() => toast.dismiss(t.id)}
              onMouseEnter={(e) => {
                e.currentTarget.style.opacity = '0.7';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.opacity = '1';
              }}
            >
              <CloseIcon width={14} height={14} />
            </button>
          )}
        </div>
      </div>
    ),
    {
      duration: 4000,
      position: 'bottom-right',
    }
  );
};

export default createToast;
